/**
 * Shared Framer Motion variants + transitions.
 *
 * Rationale for centralizing these:
 *   1. Consistency — every screen breathes at the same rhythm.
 *   2. Reduced-motion safety — flip a single flag if we ever need to disable
 *      motion globally without touching every component.
 *   3. Cheaper cognitive load — pages import `slideUp` instead of restating
 *      `{ initial: {...}, animate: {...}, exit: {...} }` five times each.
 *
 * All animation durations sit between 180 ms and 320 ms — the range where
 * motion feels responsive but not instant. Spring physics on interactive
 * elements (stiffness 400 / damping 25) gives a light "tap → snap back" feel.
 */

import type { Transition, Variants } from 'framer-motion';

/** Spring used for hover / tap on interactive cards + buttons. */
export const springInteractive: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 25,
  mass: 0.6,
};

/** Slower spring used for larger elements that need a calmer arrival. */
export const springEnter: Transition = {
  type: 'spring',
  stiffness: 260,
  damping: 28,
};

/**
 * Page-level fade + slight upward slide + micro-scale for a premium arrival.
 * Enter is slower and cushioned; exit is quick and decisive so the next page
 * feels responsive.
 */
export const pageVariants: Variants = {
  initial: { opacity: 0, y: 12, scale: 0.994 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.34, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.998,
    transition: { duration: 0.18, ease: [0.4, 0, 1, 1] },
  },
};

/** Container that staggers its direct motion children. */
export const staggerContainer = (stagger = 0.06, delayChildren = 0): Variants => ({
  initial: {},
  animate: {
    transition: { staggerChildren: stagger, delayChildren },
  },
});

/** Item to place inside a `staggerContainer` — fade + rise. */
export const staggerItem: Variants = {
  initial: { opacity: 0, y: 12 },
  animate: {
    opacity: 1,
    y: 0,
    transition: springEnter,
  },
};

/** Simple fade — for elements that shouldn't move, only appear. */
export const fadeOnly: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.24 } },
  exit: { opacity: 0, transition: { duration: 0.16 } },
};

/** Card hover / tap gesture — small, tasteful. */
export const cardGesture = {
  whileHover: { scale: 1.02, transition: springInteractive },
  whileTap: { scale: 0.98, transition: springInteractive },
} as const;

/** Icon-button gesture — a touch smaller than card gesture. */
export const iconButtonGesture = {
  whileHover: { scale: 1.06, transition: springInteractive },
  whileTap: { scale: 0.94, transition: springInteractive },
} as const;
